export interface IAddress {
    _id?: string

    /**
     * Who the parcel is addressed to, which is not necessarily the account holder.
     * Optional because `addressSchema` marks none of the three required, so older
     * addresses carry no name at all -- callers need a fallback.
     */
    name?: string
    firstName?: string
    lastName?: string

    street: string
    street2?: string
    number: string
    city: string
    state: string
    zip: string
    country: string
}

export enum UserRole {
    User = 'User',
    Sponsor = 'Sponsor',
    Admin = 'Admin',
    Bot = 'Bot'
}

export enum UserSortField {
    CreatedAt = 'createdAt',
    UpdatedAt = 'updatedAt',
    Xp = 'xp',
    Email = 'email'
}

export enum UserPermission {
    // User
    ADDRESS_VIEW = 'ADDRESS_VIEW',
    ADDRESS_MANAGE = 'ADDRESS_MANAGE',

    AFFILIATES_VIEW = 'AFFILIATE_VIEW',
    AFFILIATES_CREATE = 'AFFILIATE_CREATE',
    AFFILIATES_COLLECT = 'AFFILIATE_COLLECT',

    LEVEL_VIEW = 'LEVEL_VIEW',

    WALLET_VIEW = 'WALLET_VIEW',

    CASES_OPEN = 'CASES_OPEN',

    CLAIM_PROMO = 'CLAIM_PROMO',

    PRODUCTS_VIEW = 'PRODUCTS_VIEW',

    FAIRNESS_VIEW = 'FAIRNESS_VIEW',
    FAIRNESS_MANAGE = 'FAIRNESS_MANAGE',

    INVENTORY_VIEW = 'INVENTORY_VIEW',
    INVENTORY_SELL = 'INVENTORY_SELL',
    INVENTORY_TRADE = 'INVENTORY_TRADE',

    NOTIFICATIONS_VIEW = 'NOTIFICATIONS_VIEW',

    DEPOSITS_VIEW = 'DEPOSITS_VIEW',
    DEPOSIT = 'DEPOSIT',

    CASHOUTS_VIEW = 'CASHOUTS_VIEW',
    CASHOUT = 'CASHOUT',

    WITHDRAWALS_VIEW = 'WITHDRAWALS_VIEW',
    WITHDRAW = 'WITHDRAW',

    // Supplier programme. User-tier: the `/secure/supply` routes are permission-gated
    // only, with no Admin role requirement.
    SUPPLY_VIEW = 'SUPPLY_VIEW',
    SUPPLY_CLAIM = 'SUPPLY_CLAIM',

    // Moderator

    // Admin
    USER_VIEW = 'USER_VIEW',
    USER_MANAGE = 'USER_MANAGE',
    USER_MANAGE_ROLES = 'USER_MANAGE_ROLES',
    USER_MANAGE_PERMISSIONS = 'USER_MANAGE_PERMISSIONS',
    USER_SET = 'USER_SET',

    FAIRNESS_MANAGE_ADMIN = 'FAIRNESS_MANAGE_ADMIN',

    MANAGE_PROMO = 'MANAGE_PROMO',

    WALLET_MANAGE = 'WALLET_MANAGE',
    INVENTORY_MANAGE = 'INVENTORY_MANAGE',

    CASES_MANAGE = 'CASES_MANAGE',

    TRANSACTIONS_MANAGE = 'TRANSACTIONS_MANAGE',
    TRANSACTIONS_REFUND = 'TRANSACTIONS_REFUND',

    PRODUCTS_MANAGE = 'PRODUCTS_MANAGE',
    PRODUCTS_UPDATE_PRICE = 'PRODUCTS_UPDATE_PRICE',

    SETTINGS_VIEW = 'SETTINGS_VIEW',
    SETTINGS_MANAGE = 'SETTINGS_MANAGE',

    MAINTENANCE_VIEW = 'MAINTENANCE_VIEW',
    MAINTENANCE_MANAGE = 'MAINTENANCE_MANAGE',

    STATS_VIEW = 'STATS_VIEW',

    SUPPLY_MANAGE = 'SUPPLY_MANAGE',

    // Other
    DEMO_ACCESS = 'DEMO_ACCESS'
}

export interface IStats {
    wagered: number
    biggest_win: number
    favourite_case: {
        name: string
        image?: string
    }
}

export interface IUser {
    _id: string

    email: string
    steamId: string

    password?: string

    role: UserRole
    permissions: UserPermission[]

    xp: number

    freeShippingTokens: number

    addresses: IAddress[]

    details: {
        dname: string
        name?: string
        dob?: string
        gender?: string
        mother?: string

        phone?: string
        photo?: string

        // Affiliate code the account registered under. Written once by
        // `AffiliatesService.addRegistration` and never overwritten, so it is the
        // permanent attribution -- unlike `affiliateCode` below.
        referral?: string
    }

    stats: IStats

    two_factor: {
        enabled: boolean
        secret?: string
    }

    // The affiliate code currently attached for the deposit bonus. It is pending,
    // not permanent: the first credited deposit unsets it and sets
    // `usedDepositBonus`, after which no new code can be applied. Absent here
    // therefore means "already spent or never set", not "never referred" --
    // `details.referral` is the field that answers that.
    affiliateCode?: string
    usedDepositBonus?: boolean
    // Set permanently the first time the account is credited -- a real deposit, a
    // promo credit, or a positive admin wallet adjustment. Deposit-gated routes read
    // it, and nothing in the product clears it again.
    hasDeposited?: boolean

    verified?: boolean
    banned?: boolean
    // Release date of a temporary ban; null or absent while banned means permanent.
    bannedUntil?: string | null

    createdAt?: string
}
